import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Loader2, UserCircle2, AlertTriangle } from "lucide-react";
import { sessionManager } from "@/utils/sessionManager";
import TherapistVerificationForm from "@/components/therapist/TherapistVerificationForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useIdleLogoutWithWarning } from "@/hooks/useIdleLogoutWithWarning";

const TherapistVerification = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [userData, setUserData] = useState({
    id: "",
    email: "",
    name: "",
    firstName: "",
    lastName: "",
    role: "",
    profileImageUrl: "",
    connectionKey: "",
    websiteUrl: "",
    showActivity: "no",
    country: "",
    language: "",
    languageSecondary: "",
    profileBio: ""
  });

  // Idle logout hook for therapists only
  const { showWarning, countdown } = useIdleLogoutWithWarning({
    logout: async () => {
      await supabase.auth.signOut();
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      navigate('/auth');
    },
  });

  useEffect(() => {
    let isMounted = true;
    
    async function loadUserData() {
      try {
        if (!isMounted) return;
        setLoading(true);
        setLoadingError(false);
        
        // First verify the user session is valid
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // Use React Router navigation with redirect param
          if (window.location.pathname !== '/auth') {
            navigate('/auth', { state: { redirectTo: '/therapist/verify' } });
          }
          return;
        }
        
        // Then verify the user role
        const hasValidRole = await sessionManager.verifyUserRole(navigate);
        if (!hasValidRole) {
          console.log("Invalid or missing role detected - redirecting");
          if (isMounted) {
            setLoading(false);
            setLoadingError(true);
          }
          return;
        }
        
        // Get user profile data
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', session.user.id)
          .single();
        
        if (profileError) throw profileError;

        // Get user role
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .single();
        
        if (roleError) throw roleError;
        
        if (profileData && roleData && isMounted) {
          setUserData({
            id: session.user.id,
            email: session.user.email || "",
            name: profileData.first_name + " " + profileData.last_name,
            firstName: profileData.first_name || "",
            lastName: profileData.last_name || "",
            role: roleData.role,
            profileImageUrl: profileData.profile_image_url || "",
            connectionKey: profileData.connection_key || "",
            websiteUrl: profileData.website_url || "",
            showActivity: profileData.show_activity ? "yes" : "no",
            country: profileData.country || "",
            language: profileData.language || "",
            languageSecondary: profileData.language_secondary || "",
            profileBio: profileData.profile_bio || ""
          });
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Error loading user data:", error);
        setLoadingError(true);
        toast.error("Failed to load your profile data");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    
    loadUserData();
    
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const getInitials = () => {
    if (userData.firstName && userData.lastName) {
      return `${userData.firstName[0]}${userData.lastName[0]}`.toUpperCase();
    } else if (userData.name) {
      return userData.name.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  // Handle retry if loading failed
  const handleRetryLoading = () => {
    setLoading(true);
    setLoadingError(false);
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA]" />
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#E8F0F8] to-white flex items-center justify-center p-4">
        <Card className="max-w-md mx-auto shadow-lg backdrop-blur-sm border border-red-200 bg-white">
          <CardHeader className="bg-gradient-to-r from-red-50 to-white rounded-t-lg">
            <CardTitle className="text-xl text-red-600">Profile Loading Error</CardTitle>
            <CardDescription>We encountered an issue loading your profile</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <p className="mb-4">This could be due to a network issue or your session may have expired.</p>
            <div className="flex justify-between">
              <button 
                onClick={() => navigate('/auth')} 
                className="px-4 py-2 bg-[#E8F0F8] hover:bg-[#E8F0F8]/80 rounded-md transition-colors"
              >
                Go Home
              </button>
              <button 
                onClick={handleRetryLoading} 
                className="px-4 py-2 bg-[#3BAFDA] hover:bg-[#333B44] text-white rounded-md transition-colors"
              >
                Try Again
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full">
      {/* Idle warning modal */}
      <Dialog open={showWarning}>
        <DialogContent className="bg-gradient-to-br from-yellow-50 to-white border-yellow-200 shadow-2xl">
          <div className="flex flex-col items-center text-center space-y-4">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mb-2 animate-bounce" />
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-yellow-700">Are you still there?</DialogTitle>
              <DialogDescription className="text-base text-yellow-900">
                For your security, you will be logged out in
                <span className="mx-2 px-3 py-1 rounded-lg bg-yellow-100 text-yellow-700 font-extrabold text-xl border border-yellow-300 animate-pulse">
                  {countdown}
                </span>
                seconds due to inactivity.<br />
                <span className="text-sm text-yellow-700">Click or press any key to stay logged in.</span>
              </DialogDescription>
            </DialogHeader>
          </div>
        </DialogContent>
      </Dialog>
      {/* Full-screen blue gradient background */}
      <div className="fixed inset-0 w-full h-full bg-gradient-to-b from-[#E8F0F8] to-white z-0" />
      <div className="relative z-10 min-h-screen w-full">
        <img src="https://app.clamalo.com/images/1.png" alt="Clou Background" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-10 w-[60vw] max-w-[600px] pointer-events-none select-none z-0" />
        <div className="container mx-auto px-4 py-8 relative z-10">
          <Card className="shadow-lg backdrop-blur-sm border border-[#CCCCCC] bg-white">
            <CardHeader className="bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between w-full">
                <div className="flex items-center space-x-3">
                  <UserCircle2 className="h-8 w-8 text-[#3BAFDA]" />
                  <div>
                    <CardTitle className="text-2xl bg-clip-text text-transparent bg-gradient-to-r from-[#333B44] to-[#3BAFDA]">
                      Therapist Verification
                    </CardTitle>
                    <CardDescription>
                      A few quick questions to help us make sure our journalers are in good hands.
                    </CardDescription>
                  </div>
                </div>
                <div className="mt-4 md:mt-0 text-right">
                  <div className="text-sm font-semibold text-[#333B44]">{userData.firstName} {userData.lastName}</div>
                  <div className="text-xs text-[#333B44]">{userData.email}</div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <TherapistVerificationForm 
                userData={userData} 
                userInitials={getInitials()} 
              />
            </CardContent>
            <CardFooter className="bg-[#E8F0F8]/50 rounded-b-lg border-t border-[#CCCCCC] px-6 py-4">
              <p className="text-sm text-muted-foreground">These details help us keep Clou a trusted and supportive space for everyone.</p>
            </CardFooter>
          </Card>
        </div>
      </div>
      <div style={{ position: 'fixed', bottom: 12, right: 16, zIndex: 50 }} className="text-xs text-[#5A636E] select-none pointer-events-none">
        V1.0.001
      </div>
    </div>
  );
};

export default TherapistVerification; 